/**
 * See https://github.com/mrdoob/three.js/pull/22654
 * https://github.com/mrdoob/three.js/blob/1ee2fca970e3afdc26e6c2a47c14e9e2b784ae48/examples/jsm/controls/DeviceOrientationControls.js
 */
// The MIT License
//
// Copyright © 2010-2024 three.js authors
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import {
  Euler,
  EventDispatcher,
  MathUtils,
  Quaternion,
  Vector3
} from 'three';

const _zee = new Vector3( 0, 0, 1 );
const _euler = new Euler();
const _q0 = new Quaternion();
const _q1 = new Quaternion( - Math.sqrt( 0.5 ), 0, 0, Math.sqrt( 0.5 ) ); // - PI/2 around the x-axis

const _changeEvent = { type: 'change' };

class DeviceOrientationControls extends EventDispatcher {

  constructor( object, domElement ) {

    super();

    this.haveRequestedPermissions = false;

    if ( window.isSecureContext === false ) {
      console.error( 'THREE.DeviceOrientationControls: DeviceOrientationEvent is only available in secure contexts (https)' );
    }

    const scope = this;

    const EPS = 0.000001;
    const lastQuaternion = new Quaternion();

    this.object = object;
    this.object.rotation.reorder( 'YXZ' );

    this.enabled = true;

    this.deviceOrientation = {};
    this.screenOrientation = 0;

    this.alphaOffset = 0; // radians

    const onDeviceOrientationChangeEvent = function ( event ) {

      scope.deviceOrientation = event;

    };

    const onScreenOrientationChangeEvent = function () {

      scope.screenOrientation = window.orientation || 0;

    };

    // The angles alpha, beta and gamma form a set of intrinsic Tait-Bryan angles of type Z-X'-Y''

    const setObjectQuaternion = function ( quaternion, alpha, beta, gamma, orient ) {

      _euler.set( beta, alpha, - gamma, 'YXZ' ); // 'ZXY' for the device, but 'YXZ' for us

      quaternion.setFromEuler( _euler ); // orient the device

      quaternion.multiply( _q1 ); // camera looks out the back of the device, not the top

      quaternion.multiply( _q0.setFromAxisAngle( _zee, - orient ) ); // adjust for screen orientation

    };

    this.connect = function () {

      onScreenOrientationChangeEvent(); // run once on load
      scope.haveRequestedPermissions = true;

      // iOS 13+

      if ( window.DeviceOrientationEvent !== undefined && typeof window.DeviceOrientationEvent.requestPermission === 'function' ) {

        window.DeviceOrientationEvent.requestPermission().then( function ( response ) {

          if ( response === 'granted' ) {

            window.addEventListener( 'orientationchange', onScreenOrientationChangeEvent );
            window.addEventListener( 'deviceorientation', onDeviceOrientationChangeEvent );

          }

        } ).catch( function ( error ) {

          console.error( 'THREE.DeviceOrientationControls: Unable to use DeviceOrientation API:', error );

        } );

      } else {

        window.addEventListener( 'orientationchange', onScreenOrientationChangeEvent );
        window.addEventListener( 'deviceorientation', onDeviceOrientationChangeEvent );

      }

      scope.enabled = true;

    };

    this.disconnect = function () {

      window.removeEventListener( 'orientationchange', onScreenOrientationChangeEvent );
      window.removeEventListener( 'deviceorientation', onDeviceOrientationChangeEvent );

      scope.enabled = false;

    };

    this.update = function () {

      if ( scope.enabled === false ) return;

      const device = scope.deviceOrientation;

      if ( device ) {

        const alpha = device.alpha ? MathUtils.degToRad( device.alpha ) + scope.alphaOffset : 0; // Z

        const beta = device.beta ? MathUtils.degToRad( device.beta ) : MathUtils.degToRad(90); // X'

        const gamma = device.gamma ? MathUtils.degToRad( device.gamma ) : 0; // Y''

        const orient = scope.screenOrientation ? MathUtils.degToRad( scope.screenOrientation ) : 0; // O

        setObjectQuaternion( scope.object.quaternion, alpha, beta, gamma, orient );

        if ( 8 * ( 1 - lastQuaternion.dot( scope.object.quaternion ) ) > EPS ) {

          lastQuaternion.copy( scope.object.quaternion );
          scope.dispatchEvent( _changeEvent );

        }

      }

    };

    this.dispose = function () {

      scope.disconnect();

    };

    if (!(window.DeviceOrientationEvent !== undefined && typeof window.DeviceOrientationEvent.requestPermission === 'function')) {
      this.connect();
    } else {
      const listener = () => {
        if (scope.haveRequestedPermissions) {
          domElement.removeEventListener('pointerdown', listener);
          return;
        }

        const css = document.createElement('style');
        css.innerText = `
dialog[open] {
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #333333;
  padding: 20px 20px;
  color: #fff;
  font-family:
    Fira Sans,
    Helvetica,
    Arial,
    sans-serif;
  border-radius: 8px;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

}

dialog[open] h3 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: var(--font-weight-light, 300);
  line-height: 1.2;
  letter-spacing: -0.05rem;
}

dialog[open] p {
  font-size: 1.1rem;
  color: #fff !important;
}

dialog[open] .buttons {
  display: flex;
  gap: 2em;
  padding-bottom: 0.5em;
}
dialog[open] .buttons button {
  background-color: #89ab23;
  color: #fff;
  padding: 8px 30px;
  text-transform: uppercase;
  border: none;
  border-radius: 5px;
}

dialog[open] .buttons button:hover,
dialog[open] .buttons button:active {
  cursor: pointer;
  filter: brightness(90%);
}

dialog[open] img {
  width: 125px;
}
`;

        const dialog = document.createElement('dialog');
        domElement.parentNode.appendChild(dialog);

        const title = document.createElement('h3');
        title.innerText = 'Enable DeviceMotion?';
        const explanation = document.createElement('p');
        explanation.innerText = 'Visrez requires you to enable DeviceMotion to look around';

        const grant = document.createElement('button');
        grant.type = 'button';
        grant.innerText = 'Enable';
        grant.addEventListener('click', () => {
          dialog.close();
          dialog.remove();
          scope.connect();
          scope.haveRequestedPermissions = true;
        });

        const deny = document.createElement('button');
        deny.type = 'button';
        deny.innerText = 'Disable';
        deny.addEventListener('click', () => {
          dialog.close();
          dialog.remove();
          scope.haveRequestedPermissions = true;
        });

        const buttons = document.createElement('div');
        buttons.className = 'buttons';
        buttons.appendChild(grant);
        buttons.appendChild(deny);

        const logo = document.createElement('img');
        logo.src = '/icons/visrez-logo-text-white.svg';

        dialog.appendChild(css);
        dialog.appendChild(title);
        dialog.appendChild(explanation);
        dialog.appendChild(buttons);
        dialog.appendChild(logo);

        dialog.showModal();
      };
      domElement.addEventListener('pointerdown', listener);
    }
  }

}

export { DeviceOrientationControls };
